import React, { useState, createRef } from "react";
import {
  Carousel,
  getCurrentViewport,
  ICarouselProps,
  Button
} from "fr-ito-web-react/react/esm";
import { BreakPointProp, joinClasses } from "ds-core/utils";
import { isClient } from "@util/analytics/utils";
import { ECButtonIcon } from "@common/index";
import styles from "./styles.module.css";

const getElementVerticalCenterInParent = (
  element: Element,
  parent: Element
) => {
  const elementRect = element.getBoundingClientRect();
  const parentRect = parent.getBoundingClientRect();
  return elementRect.top - parentRect.top + elementRect.height / 2;
};

export enum ARROW_INCREMENT {
  SINGLE = "single",
  PAGE = "page"
}

export type CarouselArrowIncrement =
  | typeof ARROW_INCREMENT.SINGLE
  | typeof ARROW_INCREMENT.PAGE;

export interface ECCarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * @en Array of carousel slides
   * @required
   */
  children: React.ReactNode | React.ReactNode[];
  /**
   * @en The index of the active slide in the carousel.
   * @default 0
   */
  index?: number;
  /**
   * @en Callback function triggered when the active slide index changes.
   * @param index - The new active slide index.
   */
  onIndexChange?: (index: number) => void;
  /**
   * @en Number of slides to display in the carousel preview.
   */
  displayCount?: BreakPointProp<number> | number;
  /**
   * @en Carousel Indicator
   */
  indicator?: BreakPointProp<React.ReactNode> | React.ReactNode;
  /**
   * @en Indicator position in the carousel.
   * @en "outside" - indicator is outside the carousel
   * @en "inside" - indicator is inside the carousel
   */
  indicatorPosition?: "outside" | "inside";
  /**
   * @en Label for the skip button.
   * @default "SKIP"
   */
  skipButtonLabel?: string;
  /**
   * @en ID to skip to when the skip button is clicked.
   */
  skipToId?: string | null;
  /**
   * @en Accessibility label for the carousel.
   * @required
   */
  carouselArialabel?: string;
  /**
   * @en Whether to show arrows in the carousel.
   */
  hasArrows?: boolean | BreakPointProp<boolean>; // default: true
  /**
   * @en Callback function triggered when an arrow is clicked.
   */
  onArrowTrigger?: (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => void;
  /**
   * @en Defines the increment for arrow navigation.
   * @en "single" - moves one slide at a time
   * @en "page" - moves the number of slides defined in displayCount
   */
  arrowsIncrement?:
    | BreakPointProp<CarouselArrowIncrement>
    | CarouselArrowIncrement;
  /**
   * @en Selector for the element where the arrows should be positioned.
   * @en This is useful when you want to position arrows relative to a specific element within the carousel.
   */
  arrowPositionSelector?: string | null;
  /**
   * @en Keyboard navigation mode.
   */
  keyboardNavigation?: "item" | "arrow";
  /**
   * @en Specifies whether ECCarousel is in loop mode or not
   */
  infiniteLoop?: boolean;
  swiperOptions?: ICarouselProps["swiperOptions"];
  swiperEvents?: ICarouselProps["swiperEvents"];
  /**
   * @en Specifies whether ECCarousel requires an additional edge padding on sm screens.
   */
  addEdgePaddingOnSM?: boolean;
}

type PartialSkipLinkProps =
  | { element: "a"; href: string }
  | { element: "button"; onClick: (_: React.SyntheticEvent) => void };

export const ECCarousel: React.FC<ECCarouselProps> = ({
  children,
  index,
  onIndexChange,
  displayCount = 1,
  indicator,
  skipButtonLabel = "SKIP",
  skipToId = null,
  carouselArialabel,
  onArrowTrigger,
  hasArrows = true,
  arrowsIncrement = ARROW_INCREMENT.SINGLE,
  arrowPositionSelector,
  keyboardNavigation = "item",
  swiperOptions: userSwiperOptions,
  swiperEvents: userSwiperEvents,
  infiniteLoop = false,
  className,
  addEdgePaddingOnSM = false,
  ...props
}) => {
  const viewportSize = getCurrentViewport();
  const [activeIndex, setActiveIndex] = useState(index);
  const [swiperInstance, setSwiperInstance] = useState<any>(null);
  const lastElementRef = createRef<HTMLDivElement>();
  const [isHidden, setIsHidden] = useState(true);
  const carouselParentRef = React.useRef<HTMLDivElement>(null);
  const [isAtStart, setIsAtStart] = React.useState(true); // State to track if at the first slide
  const [isAtEnd, setIsAtEnd] = React.useState(false); // State to track if at the last slide
  const currentViewport = getCurrentViewport();

  const updateNavigationState = (swiper: any) => {
    setIsAtStart(swiper.isBeginning);
    setIsAtEnd(swiper.isEnd);
  };
  // To show the overlay (e.g., when skip button is focused)
  const handleSkipFocus = () => setIsHidden(false);

  // To hide the overlay (e.g., when skip loses focus)
  const handleSkipBlur = () => setIsHidden(true);

  // Breakpoint Prop Handling
  const isBreakpointShape = (value: any) =>
    typeof value === "object" &&
    value !== null &&
    "sm" in value &&
    "md" in value &&
    "lg" in value;
  const getBreakPointPropValue = (breakPointPropValue: any) => {
    if (isBreakpointShape(breakPointPropValue)) {
      return breakPointPropValue[viewportSize];
    }
    return breakPointPropValue;
  };

  //SKIP Button
  const onSkipClick = () => {
    const newLastElementRef = lastElementRef.current;
    if (newLastElementRef) {
      newLastElementRef.focus();
    }
  };
  const skipLinkProps: PartialSkipLinkProps = skipToId
    ? { href: `#${skipToId}`, element: "a" }
    : { onClick: onSkipClick, element: "button" };
  const skipButton = (
    <>
      <Button
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...skipLinkProps}
        className={styles.carouselSkip}
        size="small"
        onFocus={handleSkipFocus}
        onBlur={handleSkipBlur}
      >
        {skipButtonLabel}
      </Button>
      <div
        className={joinClasses(
          styles.carouselOverlay,
          isHidden && styles.carouselOverlayHidden
        )}
      />
    </>
  );

  //Arrow Buttons
  const prevButton = (
    <ECButtonIcon
      tabIndex={keyboardNavigation === "item" ? -1 : 0}
      className={joinClasses(
        styles.carouselNavigationButton,
        styles.carouselNavigationButtonPrev
      )}
      element="button"
      aria-label="arrow left"
      icon="doubleArrowLeft"
      onClick={(e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        e.preventDefault();
        if (swiperInstance) {
          swiperInstance.slidePrev();
          setIsAtStart(swiperInstance.isBeginning);
          setIsAtEnd(swiperInstance.isEnd);
        }
        if (onArrowTrigger) onArrowTrigger(e);
      }}
    />
  );
  const nextButton = (
    <ECButtonIcon
      tabIndex={keyboardNavigation === "item" ? -1 : 0}
      className={joinClasses(
        styles.carouselNavigationButton,
        styles.carouselNavigationButtonNext
      )}
      icon="doubleArrowRight"
      element="button"
      aria-label="arrow right"
      onClick={(e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        e.preventDefault();
        if (swiperInstance) {
          swiperInstance.slideNext();
          setIsAtStart(swiperInstance.isBeginning);
          setIsAtEnd(swiperInstance.isEnd);
        }
        if (onArrowTrigger) onArrowTrigger(e);
      }}
    />
  );

  const updateArrowOffset = () => {
    if (!carouselParentRef.current || !arrowPositionSelector) return;
    const frameElement = carouselParentRef.current;
    const targetElement = frameElement.querySelector(arrowPositionSelector);
    const prevButtonEl = frameElement.querySelector(
      ".carousel__navigation-button--prev"
    );
    const nextButtonEl = frameElement.querySelector(
      ".carousel__navigation-button--next"
    );

    if (targetElement) {
      const offset = getElementVerticalCenterInParent(
        targetElement,
        frameElement
      );
      const offsetPx = `calc(${offset}px - 22px)`;
      if (prevButtonEl instanceof HTMLElement) {
        prevButtonEl.style.top = offsetPx;
      }
      if (nextButtonEl instanceof HTMLElement) {
        nextButtonEl.style.top = offsetPx;
      }
    }
  };
  React.useEffect(() => {
    if (swiperInstance) {
      const resizeObserver = new ResizeObserver(() => {
        updateArrowOffset();
        setIsAtEnd(swiperInstance.isEnd);
      });

      updateArrowOffset();
      resizeObserver.observe(swiperInstance.el);
      window.addEventListener("resize", updateArrowOffset);
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("resize", updateArrowOffset);
      };
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAtEnd, isAtStart, swiperInstance, arrowPositionSelector]);

  const getSlidesPerViewOptions = () => {
    if (typeof displayCount === "number") {
      return { slidesPerView: displayCount };
    }
    if (isClient) {
      return {
        breakpoints: {
          320: { slidesPerView: (displayCount as BreakPointProp<number>)?.sm },
          600: { slidesPerView: (displayCount as BreakPointProp<number>)?.md },
          960: { slidesPerView: (displayCount as BreakPointProp<number>)?.lg }
        }
      };
    }
    return { slidesPerView: displayCount[currentViewport] };
  };

  return (
    <div
      ref={carouselParentRef}
      className={joinClasses(styles.carouselContainer, className)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {keyboardNavigation === "item" && skipButton}
      {((!isAtStart && getBreakPointPropValue(hasArrows)) || infiniteLoop) &&
        prevButton}
      <Carousel
        aria-label={carouselArialabel ?? undefined}
        activeIndex={activeIndex || 0}
        infiniteLoop={infiniteLoop}
        swiperOptions={{
          ...getSlidesPerViewOptions(),
          slidesPerGroup: (() => {
            const increment = getBreakPointPropValue(arrowsIncrement);
            if (increment === ARROW_INCREMENT.PAGE) {
              return getBreakPointPropValue(displayCount);
            }
            return 1;
          })(),
          ...userSwiperOptions
        }}
        swiperEvents={{
          ...userSwiperEvents,
          init: (swiper) => {
            setSwiperInstance(swiper);
            updateNavigationState(swiper);
            if (userSwiperEvents?.init) {
              userSwiperEvents.init(swiper);
            }
          },
          transitionEnd: (swiper) => {
            updateNavigationState(swiper);
            if (userSwiperEvents?.transitionEnd) {
              userSwiperEvents.transitionEnd(swiper);
            }
          },
          touchEnd: (swiper, event) => {
            requestAnimationFrame(() => {
              updateNavigationState(swiper);
            });
            if (userSwiperEvents?.touchEnd) {
              userSwiperEvents.touchEnd(swiper, event);
            }
          }
        }}
        onSlideChange={(swiper) => {
          updateNavigationState(swiper);
          setActiveIndex(swiper.realIndex);
          if (onIndexChange) {
            onIndexChange(swiper.realIndex);
          }
        }}
        carouselIndicator={getBreakPointPropValue(indicator)}
        className={addEdgePaddingOnSM && styles.carouselWithPaddingLeft}
      >
        {children}
      </Carousel>
      {((!isAtEnd && getBreakPointPropValue(hasArrows)) || infiniteLoop) &&
        nextButton}
      {!skipToId && (
        <div ref={lastElementRef} tabIndex={-1} aria-hidden="true" />
      )}
    </div>
  );
};
